/**
 * Control Centre entry page: five large cards, today only.
 * Orders, Drivers, Compliance, Maintenance, Telematics (default order; the
 * viewer can reorder them, saved per browser). Each card shows one headline
 * figure and at most a few things that need action today, each linking to the
 * tab and filter that shows them. The rules live in utils/controlCentreCards.
 */

import { useEffect, useMemo, useRef, useState, type ElementType, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronRight, Package, Shield, Truck, Users, Wrench } from 'lucide-react';
import DonutChart from '../DonutChart';
import { useDataFreshness } from '../../hooks/useDataFreshness';
import { useControlCentreToday } from '../../hooks/useControlCentreToday';
import { useOrdersDailyProjection } from '../../hooks/useOrdersDailyProjection';
import { buildFreshnessRows, type FreshnessRow, type FreshnessSystemId } from '../../utils/dataFreshness';
import { londonDateString } from '../../utils/ordersDailyPresentation';
import {
  CARD_LINKS,
  CARD_TITLES,
  DEFAULT_CARD_ORDER,
  NOTHING_NEEDS_ATTENTION,
  buildComplianceModel,
  buildDriversModel,
  buildMaintenanceModel,
  buildOrdersLanes,
  buildTelematicsModel,
  clearCardOrder,
  complianceChartData,
  formatLanePct,
  freshnessStamp,
  loadCardOrder,
  moveCard,
  ordersNothingNeedsAttention,
  saveCardOrder,
  type CardId,
  type CardTarget,
  type ExceptionLine,
  type Figure,
  type NavSection,
  type TelematicsTotalsLike,
} from '../../utils/controlCentreCards';

export interface ControlCentreCardsProps {
  onNavigate?: (section: NavSection, highlightKPI?: string) => void;
  useMockData?: boolean;
  /** The server says this org gets Order Flow Intelligence (GET /orders/v1/availability). */
  ordersDailyEnabled?: boolean;
  /** The server's reason the board isn't available yet, when it gave one. */
  ordersUnavailableMessage?: string;
  telematicsTotals?: TelematicsTotalsLike | null;
  /**
   * The Control Centre is the page on screen. The app keeps visited pages
   * mounted but hidden, so reading and polling stop when this is false.
   */
  active?: boolean;
}

const CARD_ICONS: Record<CardId, { icon: ElementType; color: string }> = {
  orders: { icon: Package, color: 'text-chart-5' },
  drivers: { icon: Users, color: 'text-chart-3' },
  compliance: { icon: Shield, color: 'text-chart-3' },
  maintenance: { icon: Wrench, color: 'text-chart-2' },
  telematics: { icon: Truck, color: 'text-chart-1' },
};

const LINK_LABELS: Record<CardId, string> = {
  orders: 'Open Orders',
  drivers: 'Open Drivers',
  compliance: 'Open Drivers',
  maintenance: 'Open Maintenance',
  telematics: 'Open Telematics',
};

const NOT_IN_DEMO = 'Not in demo data';

function figureText(f: Figure, demo: boolean): string {
  if (f.state === 'known') return f.value.toLocaleString('en-GB');
  if (f.state === 'loading') return '…';
  return demo ? 'Not in demo' : 'Unknown';
}

function Headline({ figure, label, demo, onClick }: { figure: Figure; label: string; demo: boolean; onClick?: () => void }) {
  const text = figureText(figure, demo);
  const body = (
    <>
      <span className="text-5xl font-black text-white tabular-nums">{text}</span>
      <span className="ml-3 text-base text-muted-foreground">{label}</span>
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className="text-left hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded">
      {body}
    </button>
  ) : (
    <div>{body}</div>
  );
}

/** Exception lines that need action (count above zero) or could not be worked out. Zero lines are not shown. */
function ExceptionList({ lines, onGo, demo }: { lines: readonly ExceptionLine[]; onGo: (t: CardTarget) => void; demo: boolean }) {
  const shown = lines.filter((l) => l.count == null || l.count > 0);
  if (shown.length === 0) return null;
  return (
    <ul className="space-y-2">
      {shown.map((l) => (
        <li key={l.id}>
          <button
            type="button"
            onClick={() => onGo(l.target)}
            data-testid={`line-${l.id}`}
            className="group inline-flex items-center gap-2 text-left text-base text-foreground hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded"
          >
            <span>{l.text}:</span>
            <span className={`font-bold tabular-nums ${l.count == null ? 'text-muted-foreground' : 'text-warning'}`}>
              {l.count == null ? (demo ? 'Not in demo' : 'Unknown') : l.count.toLocaleString('en-GB')}
            </span>
            <ChevronRight className="w-4 h-4 text-ring opacity-70 group-hover:opacity-100" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

const Nothing = () => <p className="text-base font-medium text-success" data-testid="nothing-needs-attention">{NOTHING_NEEDS_ATTENTION}</p>;

interface ShellProps {
  id: CardId;
  stamp: string;
  target: CardTarget;
  onGo: (t: CardTarget) => void;
  reorder: { active: boolean; index: number; count: number; move: (dir: 'up' | 'down') => void };
  children: ReactNode;
}

function CardShell({ id, stamp, target, onGo, reorder, children }: ShellProps) {
  const { icon: Icon, color } = CARD_ICONS[id];
  const title = CARD_TITLES[id];
  return (
    <section
      data-testid={`card-${id}`}
      aria-labelledby={`cc-card-${id}-title`}
      className="relative overflow-hidden bg-background/55 rounded-2xl p-6 border border-border/60 shadow-xl min-h-88 flex flex-col"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/10 rounded-xl"><Icon className={`w-6 h-6 ${color}`} aria-hidden /></div>
          <h3 id={`cc-card-${id}-title`} className="text-lg font-bold text-white">{title}</h3>
        </div>
        <div className="flex items-start gap-3">
          {reorder.active && (
            <div className="flex gap-1" role="group" aria-label={`Move ${title}`}>
              <button
                type="button"
                data-move={`${id}-up`}
                onClick={() => reorder.move('up')}
                disabled={reorder.index === 0}
                aria-label={`Move ${title} up`}
                className="p-2 rounded-lg bg-secondary text-white hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              >
                <ArrowUp className="w-4 h-4" aria-hidden />
              </button>
              <button
                type="button"
                data-move={`${id}-down`}
                onClick={() => reorder.move('down')}
                disabled={reorder.index === reorder.count - 1}
                aria-label={`Move ${title} down`}
                className="p-2 rounded-lg bg-secondary text-white hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              >
                <ArrowDown className="w-4 h-4" aria-hidden />
              </button>
            </div>
          )}
          <p className="text-xs text-muted-foreground text-right pt-1" data-testid={`stamp-${id}`}>{stamp}</p>
        </div>
      </div>
      <div className="flex-1 space-y-4">{children}</div>
      <div className="mt-5 pt-3 border-t border-border/50 flex justify-end">
        <button
          type="button"
          onClick={() => onGo(target)}
          data-testid={`open-${id}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-ring hover:text-ring focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded"
        >
          {LINK_LABELS[id]}
          <ChevronRight className="w-4 h-4" aria-hidden />
        </button>
      </div>
    </section>
  );
}

export default function ControlCentreCards({
  onNavigate,
  useMockData = false,
  ordersDailyEnabled = false,
  ordersUnavailableMessage,
  telematicsTotals,
  active = true,
}: ControlCentreCardsProps) {
  const demo = useMockData;
  const londonToday = londonDateString();
  const showOrders = ordersDailyEnabled || demo;

  const go = (t: CardTarget) => onNavigate?.(t.section, t.kpi);

  // ── Order of the cards (per browser) ──
  const [order, setOrder] = useState<CardId[]>(() => loadCardOrder());
  const [reordering, setReordering] = useState(false);
  const [announce, setAnnounce] = useState('');
  const focusAfterMove = useRef<{ id: CardId; dir: 'up' | 'down' } | null>(null);

  const move = (id: CardId, dir: 'up' | 'down') => {
    const next = moveCard(order, id, dir);
    if (next.every((v, i) => v === order[i])) return;
    setOrder(next);
    saveCardOrder(next);
    focusAfterMove.current = { id, dir };
    setAnnounce(`${CARD_TITLES[id]} moved ${dir}, now number ${next.indexOf(id) + 1} of ${next.length}`);
  };
  const reset = () => {
    clearCardOrder();
    setOrder([...DEFAULT_CARD_ORDER]);
    setAnnounce('Card order reset');
  };
  useEffect(() => {
    const f = focusAfterMove.current;
    if (!f) return;
    focusAfterMove.current = null;
    const pick = (d: string) => document.querySelector<HTMLButtonElement>(`[data-move="${f.id}-${d}"]`);
    const wanted = pick(f.dir);
    (wanted && !wanted.disabled ? wanted : pick(f.dir === 'up' ? 'down' : 'up'))?.focus();
  }, [order]);

  // ── Data ──
  // Only the feed records are needed for the stamps, not the activity series.
  const freshness = useDataFreshness(!demo && active, { activity: false });
  const today = useControlCentreToday(!demo && active, londonToday);
  const orders = useOrdersDailyProjection(londonToday, londonToday, {
    enabled: showOrders,
    demo,
    autoRefresh: active,
    // Open work from earlier days: the Orders tab reads it too when it shows Today.
    includeBacklog: true,
  });
  // Catch up when the page comes back on screen.
  const wasActive = useRef(active);
  const refreshOrders = orders.refresh;
  useEffect(() => {
    if (active && !wasActive.current && showOrders) void refreshOrders?.();
    wasActive.current = active;
  }, [active, showOrders, refreshOrders]);

  const now = Date.now();
  const stampFor = (id: FreshnessSystemId, onlySource?: string): string => {
    if (demo) return 'Demo data';
    // Until the feed records arrive the time is not known yet, not unknown.
    if (freshness.loading) return '';
    const feeds = freshness.feeds ?? [];
    const enabled = freshness.enabledSources;
    const rows: FreshnessRow[] = onlySource
      ? buildFreshnessRows(
          feeds.filter((f) => f.source.toLowerCase() === onlySource),
          enabled.filter((s) => s.toLowerCase() === onlySource),
          now,
        )
      : buildFreshnessRows(feeds, enabled, now);
    return freshnessStamp(rows.find((r) => r.id === id), now);
  };

  // Minute resolution: the lanes only change on the minute.
  const nowMinute = Math.floor(now / 60_000);
  const lanes = useMemo(
    () => buildOrdersLanes({ summary: orders.summary, rows: orders.rows, loading: orders.loading, now: new Date(nowMinute * 60_000) }),
    [orders.summary, orders.rows, orders.loading, nowMinute],
  );
  const driversModel = buildDriversModel({ checks: today.walkaround.data, loading: today.walkaround.loading });
  const complianceModel = buildComplianceModel({ data: today.drivers.data, loading: today.drivers.loading });
  const maintenanceModel = buildMaintenanceModel({
    summary: today.maintenance.data,
    schedule: today.schedule.data,
    todayYmd: londonToday,
    loading: today.maintenance.loading,
  });
  const telematicsModel = buildTelematicsModel(demo ? null : telematicsTotals);

  const renderCard = (id: CardId, index: number) => {
    const reorder = { active: reordering, index, count: order.length, move: (d: 'up' | 'down') => move(id, d) };
    switch (id) {
      case 'orders': {
        const nothing = ordersNothingNeedsAttention(lanes);
        return (
          <CardShell key={id} id={id} stamp={stampFor('orders')} target={CARD_LINKS.orders} onGo={go} reorder={reorder}>
            {!showOrders ? (
              <p className="text-base text-foreground">
                {ordersUnavailableMessage ?? 'Today’s order figures are not available for your order system yet.'}
              </p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {lanes.map((lane) => (
                    <div key={lane.id} data-testid={`lane-${lane.id}`} className="rounded-xl bg-card/60 border border-border/50 p-4 flex flex-col gap-2">
                      <h4 className="text-sm font-semibold text-muted-foreground">{lane.title}</h4>
                      <button type="button" onClick={() => go(lane.target)} className="text-left rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                        <span className="text-4xl font-black text-white tabular-nums" data-testid={`lane-${lane.id}-figure`}>
                          {lane.id === 'etas' ? formatLanePct(lane.headline) : figureText(lane.headline, false)}
                        </span>
                        <span className="block text-sm text-foreground mt-1">{lane.headlineText}</span>
                      </button>
                      {lane.note && <p className="text-xs text-muted-foreground">{lane.note}</p>}
                      <ExceptionList lines={[lane.exception, lane.overdue].filter((l): l is ExceptionLine => !!l)} onGo={go} demo={false} />
                    </div>
                  ))}
                </div>
                {nothing && <Nothing />}
              </>
            )}
          </CardShell>
        );
      }
      case 'drivers': {
        const m = driversModel;
        const missingAny = m.headline.state === 'known' && m.headline.value > 0;
        return (
          <CardShell key={id} id={id} stamp={stampFor('drivers', 'aquarius')} target={CARD_LINKS.drivers} onGo={go} reorder={reorder}>
            <Headline figure={demo ? { state: 'unknown' } : m.headline} label="walkaround checks missing today" demo={demo} onClick={() => go(CARD_LINKS.drivers)} />
            {missingAny && (
              <p className="text-base text-foreground" data-testid="missing-names">
                {m.missingNames.map((n, i) => (
                  <span key={`${n}-${i}`}>
                    {i > 0 && ', '}
                    <button type="button" onClick={() => go(CARD_LINKS.drivers)} className="underline underline-offset-2 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded">{n}</button>
                  </span>
                ))}
                {m.moreNames > 0 && ` and ${m.moreNames} more`}
              </p>
            )}
            <ExceptionList lines={[m.shortChecks]} onGo={go} demo={demo} />
            {m.nothingNeedsAttention && <Nothing />}
            <p className="text-xs text-muted-foreground">
              From Aquarius only. Not checked against Samsara.
              {m.notConfirmed > 0 && ` ${m.notConfirmed} clock-on${m.notConfirmed === 1 ? '' : 's'} could not be confirmed as drivers and are not counted.`}
            </p>
          </CardShell>
        );
      }
      case 'compliance': {
        const m = complianceModel;
        const charts = m.summary ? complianceChartData(m.summary) : null;
        return (
          <CardShell key={id} id={id} stamp={stampFor('drivers')} target={CARD_LINKS.compliance} onGo={go} reorder={reorder}>
            <Headline figure={demo ? { state: 'unknown' } : m.headline} label="violations yesterday" demo={demo} onClick={() => go(CARD_LINKS.compliance)} />
            {m.evaluatedCaption && <p className="text-sm text-foreground" data-testid="evaluated-caption">{m.evaluatedCaption}</p>}
            <ExceptionList lines={m.lines} onGo={go} demo={demo} />
            {m.nothingNeedsAttention && <Nothing />}
            {charts && m.summary && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-2">Compliance status</h4>
                  <div className="h-56">
                    {m.summary.evaluated > 0
                      ? <DonutChart data={charts.compliance} title="" dense valueUnit="driver" />
                      : <p className="h-full flex items-center justify-center text-sm text-muted-foreground text-center px-4">No drivers evaluated today.</p>}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-2">Time allocation</h4>
                  <div className="h-56">
                    {m.summary.hoursPendingForToday
                      ? <p className="h-full flex items-center justify-center text-sm text-muted-foreground text-center px-4">Hours still updating. Drivers are still on shift.</p>
                      : <DonutChart data={charts.time} title="" dense valueUnit="hour" />}
                  </div>
                </div>
              </div>
            )}
          </CardShell>
        );
      }
      case 'maintenance': {
        const m = maintenanceModel;
        return (
          <CardShell key={id} id={id} stamp={stampFor('maintenance')} target={CARD_LINKS.maintenance} onGo={go} reorder={reorder}>
            <Headline figure={demo ? { state: 'unknown' } : m.headline} label={m.headlineLabel.toLowerCase()} demo={demo} onClick={() => go(m.headlineTarget)} />
            <ExceptionList lines={m.lines} onGo={go} demo={demo} />
            {m.nothingNeedsAttention && <Nothing />}
            <p className="text-sm text-foreground" data-testid="next-booked">
              {m.nextBooked ?? (demo ? NOT_IN_DEMO : today.schedule.loading ? 'Next booked: …' : 'Next booked: Unknown')}
            </p>
          </CardShell>
        );
      }
      case 'telematics': {
        const m = telematicsModel;
        return (
          <CardShell key={id} id={id} stamp={stampFor('telematics')} target={CARD_LINKS.telematics} onGo={go} reorder={reorder}>
            <Headline figure={m.headline} label={m.headlineLabel.toLowerCase()} demo={demo} onClick={() => go(m.headlineTarget)} />
            <ExceptionList lines={m.lines} onGo={go} demo={demo} />
            {m.nothingNeedsAttention && <Nothing />}
          </CardShell>
        );
      }
    }
  };

  return (
    <div className="space-y-4" data-testid="control-centre-cards">
      <div className="flex items-center justify-end gap-2">
        {reordering && (
          <button
            type="button"
            onClick={reset}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-white underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded"
          >
            Reset order
          </button>
        )}
        <button
          type="button"
          onClick={() => setReordering((r) => !r)}
          aria-pressed={reordering}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-secondary hover:bg-accent text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        >
          {reordering ? 'Done' : 'Reorder'}
        </button>
      </div>
      <div className="sr-only" role="status" aria-live="polite">{announce}</div>
      {order.map((id, i) => renderCard(id, i))}
    </div>
  );
}
